/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
*/
import { Params } from '@angular/router';
import { TenantService } from '@src/app/shared/tenant.service';
import { DashboardChart } from '@src/app/shared/dashboard/dashboard-widget/dashboard-chart';

import * as Highcharts from 'highcharts';
import { HttpParams } from '@angular/common/http';
import { NavRoute } from '@src/app/shared/NavRoute';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';

class sraData {

    startDate: string;
    startTime: number;

    endDate: string;
    endTime: number;

    rows: { status: string; items: number }[];
}

export class SraChart extends DashboardChart {
    dataUrl = TenantService.ajaxPath + 'agent/dashboard/sraAll';
    dataParams = new HttpParams().set('week', 0).set('show', 'myrequests');

    colors: string[] = [];

    title = "My Service Requests";

    series: { name: string, description: string, y: number, pct: number, route: NavRoute, routeParams: Params }[] = [];
    startDate = '';
    startTime: number;

    endDate = '';
    endTime: number;

    options = [
        { name: 'Previous Week', action: this.previousWeek.bind(this) },
        { name: 'Following Week', action: this.nextWeek.bind(this) }
    ];

    previousWeek() {
        const currWeek = this.dataParams.get('week');
        this.dataParams = this.dataParams.set('week', Number(currWeek) - 1);
        this.getData();
    }

    resetChart() {
        this.colors = [];
        this.series = [];
    }

    nextWeek() {
        const currWeek = this.dataParams.get('week');
        this.dataParams = this.dataParams.set('week', Number(currWeek) + 1);
        this.getData();
    }

    getSubTitle() {
        return super.getSubTitle(this.height > 200 ? ('Week ' + this.startDate + ' to ' + this.endDate) : '')
    }

    getRouteParams(state: string): Params {
        const isClosed = state === 'Resolved' || state === 'Accepted';
        return this.getShowParms(this.getBaseRouteParams(state), isClosed ? 'resolvedById' : 'ownedById') as Params;
    }

    getBaseRouteParams(state: string): Params {
        if (state === 'Planned') {
            this.colors.push('orange');
            return { subStatus: 'planned', srStatus: 'open' };
        } else if (state === 'Overdue') {
            this.colors.push('darkred');
            return { subStatus: 'overdue', srStatus: 'open' };
        } else if (state === 'Unplanned') {
            this.colors.push('red');
            return { subStatus: 'unplanned', srStatus: 'open' };
        } else if (state === 'Resolved') {
            this.colors.push('green');
            return { srStatus: 'resolved', completedAt: '><' + this.startTime + ':' + this.endTime }
        } else if (state === 'Accepted') {
            this.colors.push('darkgreen');
            return { srStatus: 'accepted', completedAt: '><' + this.startTime + ':' + this.endTime }
        }
    }

    getName(state: string): string {
        if (state === 'Planned') {
            return $localize `<b>Planned</b> Requests with actions planned to complete them`;
        } else if (state === 'Overdue') {
            return $localize `<b>Overdue</b> Requests with overdue actions not completed`;
        } else if (state === 'Unplanned') {
            return $localize `<b>Unplanned</b> Requests with no actions entered or plan to resolve them`
        } else if (state === 'Resolved') {
            return $localize `<b>Resolved</b> Requests marked completed`
        } else if (state === 'Accepted') {
            return $localize `<b>Accepted</b> Requests that are accepted as fully complete`
        }
        return $localize `<b>Unknown type</b>`;
    }     

    protected setupData(data: sraData) {
        const route = ServiceRequestPageNavRoute;
        this.startDate = data.startDate;
        this.startTime = data.startTime;
        this.endDate = data.endDate;
        this.endTime = data.endTime;

        let total = 0;
        for (const i of data.rows) {
            total += i.items;
        }
        //Simplified states...
        for (const i of data.rows) {
            if (i.items > 0) {
                const routeParams = this.getRouteParams(i.status);
                this.series.push({name: i.status, description: this.getName(i.status), y: i.items, pct: Math.round((i.items * 100) / total), route, routeParams })
            }
        }
    }

    clickEvent($event) {
        const routeInfo = ServiceRequestPageNavRoute.getListRouterLink($event.point.routeParams);
        this.router.navigate(routeInfo);
    }

    protected createChartOptions(): Highcharts.Options {
        return {
            colors: this.colors,
            chart: {
                backgroundColor: this.chartBackgroundColor,
                borderRadius: this.chartBorderRadius,
                plotBorderWidth: null,
                type: 'pie',
                height: this.height,
                width: this.width
            },
            title: this.getChartTitle(),
            subtitle: this.getSubTitle(),
            credits: { enabled: false },
            legend: { 
                enabled: this.height > 200,       // Enable the legend
                align: 'center',      // Position it on the right (optional)
                verticalAlign: 'bottom', 
                layout: 'horizontal',
                itemStyle: { fontSize: '10px' }
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    events: {
                        click: this.clickEvent.bind(this)
                    },
                    dataLabels: {
                        enabled: false,
                        format: '{point.name}',
                        style: { fontSize: 14, textAlign: 'center' },
                        distance: -30
                    },
                    tooltip: {                       
                        headerFormat: '{point.name}',
                        pointFormat: '{point.description}<br/><b>{point.y} Items {point.pct}%</b>'                      
                    },
                    showInLegend: true
                }
            },
            series: [{
                name: 'Request Analysis',
                colorByPoint: true,
                data: this.series
            }]
        } as unknown as Highcharts.Options;
    }
}
